<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import markUrl from '@/assets/brand/zapio-mark.svg'
import BaseButton from '@/components/base/BaseButton.vue'

/**
 * The screen an operator sees while the session is being re-read.
 *
 * ## What it is actually fixing
 *
 * `main.ts` mounts without awaiting `hydrate()`, deliberately — waiting there holds a
 * blank page for a whole round trip, and forever if that request hangs. But `App.vue`
 * picks its layout from `route.meta.layout` and **defaults to `auth`**, and on a cold load
 * the first navigation has not resolved yet: the route is still `START_LOCATION`, whose
 * `meta` is empty. So every refresh painted the sign-in shell for the length of
 * `/admin/auth/me` and then swapped to the panel — an operator reloading `/shops` watched
 * themselves apparently get signed out and signed back in.
 *
 * ## Why it has a failure state
 *
 * Because without one it spins forever, and that was measured rather than imagined: with
 * the backend unreachable, `hydrate()` rejects, `sessionGuard` propagates it, the
 * navigation is **aborted**, and `route.matched` stays empty for good. A spinner over the
 * words "Restoring your session" is then a lie that never resolves — strictly worse than
 * the empty sign-in shell it replaced, which at least looked broken.
 *
 * The store is right to reject rather than fall through to the login form (a 500 is not an
 * invitation to type your password at a service that cannot check it), so the fix belongs
 * here: say so, and offer the one action that can help.
 *
 * A timer rather than an error prop, deliberately. Every cause ends the same way for the
 * person looking at it — a 500, a dead network, a request that hangs and never settles —
 * and the last of those produces no error to observe at all.
 *
 * ## The mark is IMPORTED, not fetched
 *
 * `index.html` draws this same splash before the bundle parses, so the mark is needed in
 * two places and must be a request in neither — it is the only thing on the screen, so a
 * late one is unmissable.
 *
 * Importing it means Vite inlines it as a data URI (it is under `assetsInlineLimit`), so
 * it arrives with the chunk that renders it. `index.html` gets the same file substituted
 * at transform time by the `zapio:inline-brand-mark` plugin. One file on disk, read twice
 * by the build — not two copies of a 294-character path waiting to drift.
 *
 * The spinner is `aria-hidden` and the status text is what a screen reader gets — a
 * rotating ring announces nothing. Under `prefers-reduced-motion` the ring stops entirely
 * rather than slowing: the text already says what is happening, and a spinner is the one
 * element where "it still moves, just less" is not a compromise anyone asked for.
 */

/**
 * Long enough that nobody on a slow connection is told the product is broken, short
 * enough that nobody watches a spinner wondering. A cold `/admin/auth/me` is tens of
 * milliseconds; anything past six seconds is not slow, it is wrong.
 */
const STALL_AFTER_MS = 6_000

const stalled = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
  timer = setTimeout(() => {
    stalled.value = true
  }, STALL_AFTER_MS)
})

// The splash unmounts the moment a route resolves, which is the ordinary path — so this
// is where the timer is cancelled on success, not in a watcher.
onBeforeUnmount(() => {
  clearTimeout(timer)
})

function retry(): void {
  window.location.reload()
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 grid place-items-center bg-canvas"
    data-testid="app-splash"
  >
    <div class="flex max-w-sm flex-col items-center gap-5 px-6 text-center">
      <div class="relative grid size-20 place-items-center">
        <span
          v-if="!stalled"
          class="splash-ring absolute inset-[-10px] rounded-full border-2 border-transparent border-t-brand-mark"
          aria-hidden="true"
        />
        <img
          :src="markUrl"
          alt=""
          width="80"
          height="80"
          class="size-20 rounded-lg shadow-overlay"
        />
      </div>

      <!--
        One live region for both states, so the change is announced rather than a second
        region appearing silently beside the first.
      -->
      <p
        role="status"
        class="text-caption text-ink-muted"
      >
        {{ stalled ? 'Still trying to reach the service.' : 'Restoring your session…' }}
      </p>

      <BaseButton
        v-if="stalled"
        variant="secondary"
        @click="retry()"
      >
        Try again
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
/*
  Named `splash-ring` rather than Tailwind's `animate-spin` because the reduced-motion
  stop below needs a selector to switch off, and `animate-spin` has none that does not
  also reach every other spinner in the panel.
*/
@keyframes splash-ring-spin {
  to {
    transform: rotate(360deg);
  }
}

.splash-ring {
  animation: splash-ring-spin var(--motion-spinner) linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .splash-ring {
    animation: none;
    /* Still a ring, still brand-coloured — just a static one, so the mark is not left
       floating in the middle of nothing while the text carries the whole message. */
    border-color: var(--brand-mark);
    opacity: 0.35;
  }
}
</style>
